import { keysOf, boot } from '@dazn/bootstrap-lib';

import { chapterNames } from '../../configuration/constants';
import { Configuration, UserModule } from '../global-modules';

// Matches:
//   '/'
//   '/en-DE'
//   '/en-DE/'
const ROOT_REGEX = /^(\/[a-z]{2}-[a-z]{2})?\/?$/i;
const isRoot = (pathname: string) =>
    ROOT_REGEX.test(pathname) || pathname.includes('index.html');

// Matches:
//   '/en-DE'
//   '/en-de'
//   '/EN-DE'
//   '/EN-de'
const LANG_REGEX = /^\/[a-z]{2}-[a-z]{2}/i;
const getChapterNameFromMap = (
    urlPath: string,
    availableChapters: boot.config.ChapterMap<string[]>,
) => {
    const pathWithoutLanguage = urlPath.replace(LANG_REGEX, '');
    const [, pageName] = pathWithoutLanguage.split('/');
    const chapters = keysOf(availableChapters);
    return chapters.filter(chapter =>
        availableChapters[chapter]!.includes(pageName),
    );
};

const getMatchingChapterNamesFromPath = (
    urlPath: string,
    authorizedMap: Configuration['chapters']['authorizedMap'],
    unauthorizedMap: Configuration['chapters']['unauthorizedMap'],
) =>
    [
        ...getChapterNameFromMap(urlPath, authorizedMap),
        ...getChapterNameFromMap(urlPath, unauthorizedMap),
    ].filter(entry => entry);

const verifyChapterAuthorization = ({
    configuration,
    isActive,
    isAuthorized,
    chapters,
    isVip,
}: {
    configuration: Configuration['chapters'];
    isActive: (statuses: string[] | undefined) => boolean;
    isAuthorized: boolean;
    chapters: boot.config.ChapterName[];
    isVip: () => boolean;
}) => {
    const { authorizedMap, unauthorizedMap, rootAuthorized, loginChapter } =
        configuration;

    const anonMatch = keysOf(unauthorizedMap).filter(chapter =>
        chapters.includes(chapter),
    );

    const authMatch = keysOf(authorizedMap).filter(chapter =>
        chapters.includes(chapter),
    );

    const authOnly = authMatch.filter(chapter => !anonMatch.includes(chapter));

    const anonOnly = anonMatch.filter(chapter => !authMatch.includes(chapter));

    const anyOnly = anonMatch.filter(chapter => authMatch.includes(chapter));

    const [authorizedChapter = null] = authOnly;

    const [anonymousChapter = null] = anonOnly;

    const [any = null] = anyOnly;

    const requiresAuth = Boolean(authorizedChapter) && !any;

    if (requiresAuth) {
        if (
            isAuthorized &&
            authorizedChapter &&
            isActive(configuration.allowedUserStatuses(authorizedChapter))
        ) {
            if (isVip() && configuration.restrictToVip(authorizedChapter)!)
                return chapterNames.CATALOG;

            return authorizedChapter;
        }

        return loginChapter;
    }

    if (anonymousChapter) {
        if (!isActive(configuration.allowedUserStatuses(anonymousChapter))) {
            return anonymousChapter;
        }
        return isAuthorized ? rootAuthorized : anonymousChapter;
    }

    if (any) {
        if (
            authorizedChapter &&
            isActive(configuration.allowedUserStatuses(authorizedChapter))
        ) {
            return authorizedChapter;
        }
        return any;
    }

    return chapters[0];
};

const getChapterInfoFromPath = ({
    configuration,
    isAuthorized,
    isActive,
    pathname,
    startupNativeCountryOrMoon,
    startupNativeCountryOrMoonOrMena,
    isVip,
}: {
    configuration: Configuration;
    isAuthorized: boolean;
    isActive: UserModule['isActive'];
    pathname: string;
    startupNativeCountryOrMoon: boot.config.NativeCountryOrMoon;
    startupNativeCountryOrMoonOrMena: boot.config.NativeCountryOrMoonOrMena;
    isVip: () => boolean;
}) => {
    const {
        rootAuthorized,
        getUnauthorized,
        allowedUserStatuses,
        authorizedMap,
        unauthorizedMap,
    } = configuration.chapters;

    if (pathname.includes(configuration.appUrlPrefix)) {
        pathname = pathname.replace(configuration.appUrlPrefix, '');
    }

    const isRootPath = isRoot(pathname);
    if (isRootPath) {
        const hasAuth =
            isAuthorized &&
            rootAuthorized &&
            isActive(allowedUserStatuses(rootAuthorized));
        const chapterName = hasAuth
            ? rootAuthorized
            : getUnauthorized(startupNativeCountryOrMoonOrMena);
        return {
            isRootPath,
            chapterName,
            verifiedChapterName: chapterName,
        };
    }
    const availableChapters = getMatchingChapterNamesFromPath(
        pathname,
        authorizedMap,
        unauthorizedMap,
    );
    const verifiedChapterName = verifyChapterAuthorization({
        configuration: configuration.chapters,
        isActive,
        isAuthorized: isAuthorized,
        chapters: availableChapters,
        isVip,
    });
    const authorizedAndAllowed =
        isAuthorized && isActive(allowedUserStatuses(verifiedChapterName));
    const chapterName =
        keysOf(authorizedAndAllowed ? authorizedMap : unauthorizedMap).find(
            cn => availableChapters.includes(cn),
        ) || availableChapters[0];

    return {
        isRootPath,
        chapterName,
        verifiedChapterName,
    };
};

export { verifyChapterAuthorization, getChapterInfoFromPath };
